import React from 'react';
import { useBrand } from '../../../app/config/brand';
import type { CaseListStatus } from '@lds-efsp/shared-contracts';

type FilterKey = CaseListStatus | 'all' | 'recentlyFiled';

interface FilterPill {
  key: FilterKey;
  label: string;
}

const FILTER_PILLS: FilterPill[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'pending', label: 'Pending' },
  { key: 'recentlyFiled', label: 'Recently Filed' },
  { key: 'needs_attention', label: 'Needs Attention' },
  { key: 'closed', label: 'Closed' },
];

interface CaseFilterBarProps {
  activeFilter: FilterKey;
  search: string;
  onFilterChange: (filter: FilterKey) => void;
  onSearchChange: (search: string) => void;
}

export const CaseFilterBar: React.FC<CaseFilterBarProps> = ({
  activeFilter,
  search,
  onFilterChange,
  onSearchChange,
}) => {
  const brand = useBrand();

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6" data-testid="case-filter-bar">
      <div className="flex flex-wrap gap-2" data-testid="filter-pills">
        {FILTER_PILLS.map((pill) => {
          const isActive = pill.key === activeFilter;
          return (
            <button
              key={pill.key}
              onClick={() => onFilterChange(pill.key)}
              className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
              style={{
                backgroundColor: isActive ? brand.colors.primary : brand.colors.bgLight,
                color: isActive ? brand.colors.onPrimary : brand.colors.textLight,
              }}
              data-testid={`filter-pill-${pill.key}`}
            >
              {pill.label}
            </button>
          );
        })}
      </div>
      <div className="relative w-full sm:w-64 sm:ml-auto">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
          fill="none"
          stroke={brand.colors.textMuted}
          viewBox="0 0 24 24"
          data-testid="search-icon"
        >
          <circle cx="11" cy="11" r="8" strokeWidth="2" />
          <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search cases..."
          aria-label="Search cases"
          className="w-full pl-10 pr-4 py-2 rounded-lg text-sm outline-none"
          style={{
            backgroundColor: brand.colors.bgLight,
            border: `1px solid ${brand.colors.border}`,
            color: brand.colors.text,
          }}
          data-testid="case-search-input"
        />
      </div>
    </div>
  );
};
